// ============================================================
// MANIC MEETER — Browser Input Handler
// ============================================================

import { C } from "./i18n";

// ── Pausable timeouts ───────────────────────────────────────
// Background tabs must not cost the player anything: a turn/minigame timeout
// that fires while the tab is hidden (or immediately on return) counts as
// "caught procrastinating" through no fault of the player. These timers pause
// while document.hidden and resume — together with the CSS timer bar — when
// the tab becomes visible again.

export interface PausableTimer {
  clear(): void;
}

function startPausableTimeout(
  timeoutMs: number,
  onFire: () => void,
  timerFill?: HTMLElement,
): PausableTimer {
  let id: number | null = null;
  let remaining = timeoutMs;
  let startedAt = 0;
  let done = false;

  const animateBar = (ms: number) => {
    if (!timerFill) return;
    // Double rAF so the current width is painted before the transition starts.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (done || document.hidden || !timerFill.parentElement) return;
        timerFill.style.transition = `width ${ms}ms linear`;
        timerFill.style.width = "0%";
      });
    });
  };

  // Freeze the bar at its current painted width (transition keeps running
  // otherwise, even though the timeout is paused).
  const freezeBar = () => {
    if (!timerFill || !timerFill.parentElement) return;
    const total = timerFill.parentElement.getBoundingClientRect().width;
    const current = timerFill.getBoundingClientRect().width;
    timerFill.style.transition = "none";
    if (total > 0) timerFill.style.width = `${(current / total) * 100}%`;
  };

  const fire = () => {
    done = true;
    document.removeEventListener("visibilitychange", onVisibility);
    onFire();
  };

  const arm = () => {
    startedAt = Date.now();
    id = window.setTimeout(fire, remaining);
    animateBar(remaining);
  };

  const pause = () => {
    if (done || id === null) return;
    window.clearTimeout(id);
    id = null;
    remaining = Math.max(0, remaining - (Date.now() - startedAt));
    freezeBar();
  };

  const onVisibility = () => {
    if (document.hidden) pause();
    else if (!done && id === null) arm();
  };

  document.addEventListener("visibilitychange", onVisibility);
  if (document.hidden) freezeBar();
  else arm();

  return {
    clear() {
      done = true;
      if (id !== null) window.clearTimeout(id);
      document.removeEventListener("visibilitychange", onVisibility);
    },
  };
}

/**
 * Wait for a button click. Shows buttons, returns the value of the clicked one.
 * On timeout returns null. Optional header text shown above buttons.
 */
export function waitForChoice(
  choices: { label: string; value: string; className?: string }[],
  container: HTMLElement,
  timeoutMs: number,
  header?: string,
): Promise<string | null> {
  return new Promise((resolve) => {
    const btnRow = document.createElement("div");
    btnRow.className = "choice-row";

    let headerEl: HTMLElement | null = null;
    let resolved = false;
    const cleanup = () => {
      if (headerEl && headerEl.parentElement) headerEl.remove();
      if (btnRow.parentElement) btnRow.remove();
      if (timerBar && timerBar.parentElement) timerBar.remove();
    };

    // Optional header (e.g. question prompt)
    if (header) {
      headerEl = document.createElement("div");
      headerEl.className = "choice-header";
      headerEl.innerHTML = header;
      container.appendChild(headerEl);
    }

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    for (const c of choices) {
      const btn = document.createElement("button");
      btn.className = "choice-btn " + (c.className ?? "");
      btn.textContent = c.label;
      btn.addEventListener("click", () => {
        if (!resolved) {
          resolved = true;
          timer.clear();
          cleanup();
          resolve(c.value);
        }
      });
      btnRow.appendChild(btn);
    }
    container.appendChild(btnRow);

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(null);
      }
    }, timerFill);
  });
}

/**
 * Character-by-character typing challenge (Brainstorming).
 * Shows the word, highlights current char. Wrong input repeats that char.
 * Returns true if completed in time, false otherwise.
 */
export function waitForCharByChar(
  word: string,
  container: HTMLElement,
  timeoutMs: number,
  onProgress?: (current: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;
    let currentIdx = 0;

    // Display
    const display = document.createElement("div");
    display.className = "char-challenge";

    function renderWord() {
      let html = "";
      for (let i = 0; i < word.length; i++) {
        if (i < currentIdx) {
          html += `<span class="char-done">${word[i]}</span>`;
        } else if (i === currentIdx) {
          html += `<span class="char-current">${word[i]}</span>`;
        } else {
          html += `<span class="char-pending">${word[i]}</span>`;
        }
      }
      display.innerHTML = html;
    }
    renderWord();
    container.appendChild(display);

    // Feedback element
    const feedback = document.createElement("div");
    feedback.className = "char-feedback";
    feedback.textContent = C().ui.minigames.typeHint;
    container.appendChild(feedback);

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      document.removeEventListener("keydown", handler);
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    const handler = (e: KeyboardEvent) => {
      if (resolved) return;
      if (e.key.length !== 1) return; // ignore special keys

      const expected = word[currentIdx].toLowerCase();
      const typed = e.key.toLowerCase();

      if (typed === expected) {
        currentIdx++;
        feedback.textContent = C().ui.minigames.progress(currentIdx, word.length);
        feedback.className = "char-feedback char-ok";
        renderWord();
        if (onProgress) onProgress(currentIdx);

        if (currentIdx >= word.length) {
          resolved = true;
          timer.clear();
          cleanup();
          resolve(true);
        }
      } else {
        feedback.textContent = C().ui.minigames.wrong(`"${word[currentIdx]}"`);
        feedback.className = "char-feedback char-wrong";
      }
    };

    document.addEventListener("keydown", handler);

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, timerFill);
  });
}

/**
 * Wait for a keypress sequence (Simon Says) with per-step visual feedback.
 */
export function waitForSequence(
  expectedSequence: string[],
  container: HTMLElement,
  timeoutMs: number,
  onProgress?: (current: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;
    const keyMap: Record<string, string> = { w: "↑", arrowup: "↑", s: "↓", arrowdown: "↓", a: "←", arrowleft: "←", d: "→", arrowright: "→" };
    let currentIdx = 0;

    // Sequence display: shows expected with highlighting
    const display = document.createElement("div");
    display.className = "sequence-display";

    function renderSequence() {
      let html = "";
      for (let i = 0; i < expectedSequence.length; i++) {
        if (i < currentIdx) {
          html += `<span class="seq-done">✓</span> `;
        } else if (i === currentIdx) {
          html += `<span class="seq-current">${expectedSequence[i]}</span> `;
        } else {
          html += `<span class="seq-pending">${expectedSequence[i]}</span> `;
        }
      }
      display.innerHTML = html;
    }
    renderSequence();
    container.appendChild(display);

    // Feedback element
    const feedback = document.createElement("div");
    feedback.className = "seq-feedback";
    feedback.textContent = C().ui.minigames.sequenceHint;
    container.appendChild(feedback);

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      document.removeEventListener("keydown", handler);
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    const handler = (e: KeyboardEvent) => {
      if (resolved) return;
      const mapped = keyMap[e.key.toLowerCase()];
      if (!mapped) return;

      const expected = expectedSequence[currentIdx];
      if (mapped === expected) {
        currentIdx++;
        feedback.textContent = C().ui.minigames.progress(currentIdx, expectedSequence.length);
        feedback.className = "seq-feedback seq-ok";
        renderSequence();
        if (onProgress) onProgress(currentIdx);

        if (currentIdx >= expectedSequence.length) {
          resolved = true;
          timer.clear();
          cleanup();
          resolve(true);
        }
      } else {
        feedback.textContent = C().ui.minigames.wrong(expected);
        feedback.className = "seq-feedback seq-wrong";
        // Reset on wrong input
        currentIdx = 0;
        renderSequence();
      }
    };

    document.addEventListener("keydown", handler);

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, timerFill);
  });
}

/**
 * Simple continue button, no timeout.
 */
export function waitForContinue(container: HTMLElement, label = "Weiter"): Promise<void> {
  return new Promise((resolve) => {
    const btn = document.createElement("button");
    btn.className = "choice-btn continue";
    btn.textContent = label;
    btn.addEventListener("click", () => {
      if (btn.parentElement) btn.remove();
      resolve();
    });
    container.appendChild(btn);
    btn.focus();
  });
}

/**
 * Buzzword Bingo: 4 buttons shown, player must click them in the correct order.
 * Returns true if all clicked in correct order within time.
 */
export function waitForBuzzwordBingo(
  words: string[],
  correctOrder: number[],
  container: HTMLElement,
  timeoutMs: number,
  onProgress?: (current: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;
    let clickIdx = 0;

    const display = document.createElement("div");
    display.className = "bingo-challenge";

    const feedback = document.createElement("div");
    feedback.className = "bingo-feedback";
    feedback.textContent = C().ui.minigames.memorising;
    container.appendChild(feedback);

    // Build a reverse map: correctOrder[pos] = wordIdx → orderLabel[wordIdx] = pos+1
    const orderLabel: Record<number, number> = {};
    for (let pos = 0; pos < correctOrder.length; pos++) {
      orderLabel[correctOrder[pos]] = pos + 1;
    }

    const buttons: HTMLButtonElement[] = [];
    for (let i = 0; i < words.length; i++) {
      const btn = document.createElement("button");
      btn.className = "choice-btn bingo-word bingo-preview";
      btn.innerHTML = `<span class="bingo-number">${orderLabel[i]}</span> ${words[i]}`;
      btn.dataset.idx = String(i);
      btn.disabled = true; // disabled during preview
      buttons.push(btn);
      display.appendChild(btn);
    }
    container.appendChild(display);

    // Preview phase: show numbers for 2.5 seconds, then hide and start clicking.
    // The play timer only starts AFTER the preview (same total budget as before)
    // so hiding the tab during either phase pauses fairly.
    const previewMs = 2500;
    let timer: PausableTimer | null = null;
    startPausableTimeout(previewMs, () => {
      if (resolved) return;
      // Hide numbers, enable buttons
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].textContent = words[i];
        buttons[i].classList.remove("bingo-preview");
        buttons[i].disabled = false;
      }
      feedback.textContent = C().ui.minigames.clickInOrder(words.length);
      feedback.className = "bingo-feedback";

      // Now attach click handlers
      for (let i = 0; i < buttons.length; i++) {
        buttons[i].addEventListener("click", () => {
          if (resolved) return;
          const expected = correctOrder[clickIdx];
          if (i === expected) {
            buttons[i].classList.add("bingo-done");
            buttons[i].disabled = true;
            clickIdx++;
            feedback.textContent = C().ui.minigames.progress(clickIdx, words.length);
            feedback.className = "bingo-feedback bingo-ok";
            if (onProgress) onProgress(clickIdx);
            if (clickIdx >= correctOrder.length) {
              resolved = true;
              timer?.clear();
              cleanup();
              resolve(true);
            }
          } else {
            feedback.textContent = C().ui.minigames.wrongOrder;
            feedback.className = "bingo-feedback bingo-wrong";
            clickIdx = 0;
            for (const b of buttons) {
              b.classList.remove("bingo-done");
              b.disabled = false;
            }
          }
        });
      }

      // Play timer (with bar) starts now — after the preview.
      timer = startPausableTimeout(timeoutMs - previewMs, () => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(false);
        }
      }, timerFill);
    });

    // Timer bar (visible but full during preview)
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };
  });
}

/**
 * Email Sortieren: show emails one by one, player picks category.
 * Returns number of correct categorizations.
 */
export function waitForEmailSort(
  emails: { subject: string; correct: string }[],
  container: HTMLElement,
  timeoutMs: number,
): Promise<number> {
  return new Promise((resolve) => {
    let resolved = false;
    let currentIdx = 0;
    let correctCount = 0;

    const display = document.createElement("div");
    display.className = "email-challenge";
    container.appendChild(display);

    const feedback = document.createElement("div");
    feedback.className = "email-feedback";
    container.appendChild(feedback);

    const btnRow = document.createElement("div");
    btnRow.className = "choice-row";
    container.appendChild(btnRow);

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (btnRow.parentElement) btnRow.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    function renderEmail() {
      if (currentIdx >= emails.length) {
        resolved = true;
        timer.clear();
        cleanup();
        resolve(correctCount);
        return;
      }
      display.innerHTML = `<div class="email-subject">📧 <strong>${emails[currentIdx].subject}</strong></div><div class="email-progress">${currentIdx + 1}/${emails.length}</div>`;
      feedback.textContent = "";
      feedback.className = "email-feedback";
    }

    function addCategoryButtons() {
      const mg = C().ui.minigames;
      const categories = [
        { label: mg.emailSpam, value: "spam", className: "email-spam" },
        { label: mg.emailImportant, value: "wichtig", className: "email-wichtig" },
        { label: mg.emailDelegate, value: "delegieren", className: "email-delegieren" },
      ];
      btnRow.innerHTML = "";
      for (const cat of categories) {
        const btn = document.createElement("button");
        btn.className = `choice-btn ${cat.className}`;
        btn.textContent = cat.label;
        btn.addEventListener("click", () => {
          if (resolved) return;
          const correct = emails[currentIdx].correct;
          if (cat.value === correct) {
            correctCount++;
            feedback.textContent = mg.correct;
            feedback.className = "email-feedback email-ok";
          } else {
            feedback.textContent = mg.correctWas(categories.find((c) => c.value === correct)?.label ?? correct);
            feedback.className = "email-feedback email-wrong";
          }
          currentIdx++;
          setTimeout(() => renderEmail(), 400);
        });
        btnRow.appendChild(btn);
      }
    }

    addCategoryButtons();
    renderEmail();

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(correctCount);
      }
    }, timerFill);
  });
}

/**
 * Code Review: show a code snippet with a bug and 3 fix options.
 * Returns true if the correct fix is selected in time.
 */
export function waitForCodeReview(
  code: string,
  options: string[],
  correctIndex: number,
  language: string,
  container: HTMLElement,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;

    const display = document.createElement("div");
    display.className = "code-review-challenge";
    container.appendChild(display);

    // Language label
    const langLabel = document.createElement("div");
    langLabel.className = "code-review-lang";
    langLabel.textContent = language;
    display.appendChild(langLabel);

    // Code block
    const codeBlock = document.createElement("pre");
    codeBlock.className = "code-review-code";
    codeBlock.textContent = code;
    display.appendChild(codeBlock);

    // Options as buttons
    const optionRow = document.createElement("div");
    optionRow.className = "code-review-options";
    for (let i = 0; i < options.length; i++) {
      const btn = document.createElement("button");
      btn.className = "choice-btn code-review-option";
      btn.textContent = `${i + 1}. ${options[i]}`;
      btn.addEventListener("click", () => {
        if (resolved) return;
        resolved = true;
        timer.clear();
        if (i === correctIndex) {
          feedback.textContent = `${C().ui.minigames.correct} ${options[i]}`;
          feedback.className = "code-review-feedback kalender-ok";
        } else {
          feedback.textContent = C().ui.minigames.correctWas(options[correctIndex]);
          feedback.className = "code-review-feedback kalender-wrong";
        }
        setTimeout(() => { cleanup(); resolve(i === correctIndex); }, 800);
      });
      optionRow.appendChild(btn);
    }
    display.appendChild(optionRow);

    const feedback = document.createElement("div");
    feedback.className = "code-review-feedback";
    feedback.textContent = C().ui.minigames.findTheFix;
    container.appendChild(feedback);

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, timerFill);
  });
}

/**
 * Kalender Tetris: Show a table of participants × time slots.
 * Player must find the participant where ALL time slots are free.
 * Returns true if correct participant selected in time.
 */
export function waitForKalenderTetris(
  participants: { name: string; slots: boolean[] }[],
  timeSlots: string[],
  correctSlot: number,
  container: HTMLElement,
  timeoutMs: number,
): Promise<boolean> {
  return new Promise((resolve) => {
    let resolved = false;

    const display = document.createElement("div");
    display.className = "kalender-challenge";
    container.appendChild(display);

    // Build table: header row with clickable time slots, then one row per participant
    const table = document.createElement("table");
    table.className = "kalender-table";
    const thead = document.createElement("tr");
    const cornerTh = document.createElement("th");
    cornerTh.textContent = "Name";
    thead.appendChild(cornerTh);
    for (let j = 0; j < timeSlots.length; j++) {
      const th = document.createElement("th");
      th.textContent = timeSlots[j];
      th.className = "kalender-slot-header";
      th.addEventListener("click", () => {
        if (resolved) return;
        resolved = true;
        timer.clear();
        if (j === correctSlot) {
          feedback.textContent = C().ui.minigames.slotCorrect(timeSlots[j]);
          feedback.className = "kalender-feedback kalender-ok";
        } else {
          feedback.textContent = C().ui.minigames.slotWrong(timeSlots[correctSlot]);
          feedback.className = "kalender-feedback kalender-wrong";
        }
        setTimeout(() => { cleanup(); resolve(j === correctSlot); }, 600);
      });
      thead.appendChild(th);
    }
    table.appendChild(thead);

    for (let i = 0; i < participants.length; i++) {
      const tr = document.createElement("tr");
      const nameTd = document.createElement("td");
      nameTd.className = "kalender-name";
      nameTd.textContent = participants[i].name;
      tr.appendChild(nameTd);
      for (let j = 0; j < participants[i].slots.length; j++) {
        const td = document.createElement("td");
        td.className = participants[i].slots[j] ? "kalender-busy" : "kalender-avail";
        td.textContent = participants[i].slots[j] ? "🔒" : "✅";
        tr.appendChild(td);
      }
      table.appendChild(tr);
    }
    display.appendChild(table);

    const feedback = document.createElement("div");
    feedback.className = "kalender-feedback";
    feedback.textContent = C().ui.minigames.slotQuestion;
    container.appendChild(feedback);

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, timerFill);
  });
}

/**
 * KI-Halluzination: the assistant's output, with some of it invented.
 *
 * The player flags claims by clicking them; a second click takes a flag back,
 * because reading a slide is a change of mind and not a commitment. As soon as
 * as many claims are flagged as there are hallucinations, the round resolves
 * and shows what was actually made up. Resolves with the number of invented
 * claims correctly flagged — partial credit, the way the inbox works: catching
 * one of two lies is worth something, just not everything.
 */
export function waitForKiHalluzination(
  statements: string[],
  hallucinations: number[],
  tool: string,
  task: string,
  container: HTMLElement,
  timeoutMs: number,
): Promise<number> {
  return new Promise((resolve) => {
    let resolved = false;
    const flagged = new Set<number>();
    const wanted = hallucinations.length;

    const display = document.createElement("div");
    display.className = "ki-challenge";
    container.appendChild(display);

    const toolLabel = document.createElement("div");
    toolLabel.className = "ki-tool";
    toolLabel.textContent = `🤖 ${tool}`;
    display.appendChild(toolLabel);

    const promptLine = document.createElement("div");
    promptLine.className = "ki-prompt";
    promptLine.textContent = `Prompt: "${task}"`;
    display.appendChild(promptLine);

    const list = document.createElement("div");
    list.className = "ki-statements";
    display.appendChild(list);

    const buttons: HTMLButtonElement[] = [];
    for (let i = 0; i < statements.length; i++) {
      const btn = document.createElement("button");
      btn.className = "choice-btn ki-statement";
      btn.textContent = statements[i];
      btn.addEventListener("click", () => {
        if (resolved) return;
        if (flagged.has(i)) {
          flagged.delete(i);
          btn.classList.remove("ki-flagged");
        } else {
          flagged.add(i);
          btn.classList.add("ki-flagged");
        }
        updateCounter();
        if (flagged.size === wanted) finish();
      });
      buttons.push(btn);
      list.appendChild(btn);
    }

    const feedback = document.createElement("div");
    feedback.className = "ki-feedback";
    container.appendChild(feedback);

    const updateCounter = () => {
      if (resolved) return;
      const left = wanted - flagged.size;
      feedback.textContent =
        left > 0
          ? C().ui.minigames.markHallucinations(left)
          : C().ui.minigames.evaluating;
      feedback.className = "ki-feedback";
    };

    // Timer bar
    const timerBar = document.createElement("div");
    timerBar.className = "timer-bar";
    const timerFill = document.createElement("div");
    timerFill.className = "timer-fill";
    timerBar.appendChild(timerFill);
    container.appendChild(timerBar);

    const cleanup = () => {
      if (display.parentElement) display.remove();
      if (feedback.parentElement) feedback.remove();
      if (timerBar.parentElement) timerBar.remove();
    };

    /** Score what is flagged, show the truth, and hand the count back. */
    const finish = () => {
      if (resolved) return;
      resolved = true;
      timer.clear();
      const hits = [...flagged].filter((i) => hallucinations.includes(i)).length;
      for (let i = 0; i < buttons.length; i++) {
        const invented = hallucinations.includes(i);
        buttons[i].classList.remove("ki-flagged");
        if (invented && flagged.has(i)) buttons[i].classList.add("ki-caught");
        else if (invented) buttons[i].classList.add("ki-missed");
        else if (flagged.has(i)) buttons[i].classList.add("ki-wrongly-flagged");
      }
      if (hits === wanted) {
        feedback.textContent = C().ui.minigames.hallucinationsCaught(hits, wanted);
        feedback.className = "ki-feedback kalender-ok";
      } else {
        feedback.textContent = C().ui.minigames.hallucinationsMissed(hits, wanted);
        feedback.className = "ki-feedback kalender-wrong";
      }
      setTimeout(() => { cleanup(); resolve(hits); }, 900);
    };

    updateCounter();

    // Out of time: whatever is flagged by now is the answer.
    const timer = startPausableTimeout(timeoutMs, () => {
      if (!resolved) finish();
    }, timerFill);
  });
}
